import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { hasCapability } from '@/lib/rbac';
import { fetchFingerprintChangeRequests } from '@/schemas/fingerprint-requests';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type Updater,
} from '@tanstack/react-table';
import { ChevronRight, Copy, ArrowUp, ArrowDown, ChevronsUpDown, Fingerprint } from 'lucide-react';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RelativeTime } from './relative-time';
import { copyToClipboard } from '@/lib/format';
import { useIsMobile } from '@/hooks/use-media-query';
import type { License } from '@/schemas/licenses';

const STATUS_TEXT_CLASS: Record<License['status'], string> = {
  active: 'text-status-active',
  expired: 'text-status-expired',
  revoked: 'text-status-revoked',
  pending: 'text-status-pending',
  permitted: 'text-status-permitted',
};

const STATUS_DOT_CLASS: Record<License['status'], string> = {
  active: 'bg-status-active',
  expired: 'bg-status-expired',
  revoked: 'bg-status-revoked',
  pending: 'bg-status-pending',
  permitted: 'bg-status-permitted',
};

const STATUS_LABEL_KEYS: Record<License['status'], string> = {
  active: 'common.status.active',
  expired: 'common.status.expired',
  revoked: 'common.status.revoked',
  pending: 'common.status.pending',
  permitted: 'common.status.permitted',
};

export function StatusWord({ status }: { status: License['status'] }) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex items-center gap-2 whitespace-nowrap text-[13.5px] font-medium ${STATUS_TEXT_CLASS[status]}`}
    >
      <span className={`h-2 w-2 rounded-full ${STATUS_DOT_CLASS[status]}`} aria-hidden="true" />
      {t(STATUS_LABEL_KEYS[status])}
    </span>
  );
}

interface LicensesTableProps {
  data: License[];
  sorting: SortingState;
  onSortingChange: (updater: Updater<SortingState>) => void;
}

function SortIndicator({ sorted }: { sorted: false | 'asc' | 'desc' }) {
  if (sorted === 'asc')
    return <ArrowUp className="h-3.5 w-3.5 text-foreground" aria-hidden="true" />;
  if (sorted === 'desc')
    return <ArrowDown className="h-3.5 w-3.5 text-foreground" aria-hidden="true" />;
  return <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" aria-hidden="true" />;
}

export function LicensesTable({ data, sorting, onSortingChange }: LicensesTableProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  // #177: a pending fingerprint-change request marks the licence row so an admin
  // notices a hardware swap without opening the queue. Only admins may read the queue.
  const { user } = useAuth();
  const canManageFingerprints = user ? hasCapability(user.role, 'manageUsers') : false;
  const pendingFingerprints = useQuery({
    queryKey: ['fingerprint-requests', 'pending'],
    queryFn: () => fetchFingerprintChangeRequests(),
    enabled: canManageFingerprints,
    staleTime: 30_000,
  });
  const pendingLicenseIds = useMemo(
    () => new Set((pendingFingerprints.data ?? []).map((r) => r.licenseId)),
    [pendingFingerprints.data]
  );

  const columns = useMemo<ColumnDef<License>[]>(
    () => [
      {
        accessorKey: 'serial',
        enableSorting: false,
        header: t('licenses.table.serial'),
        cell: ({ row }) => {
          const serial = row.original.serial;
          return (
            <div className="group flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="max-w-[220px] truncate font-mono text-[13.5px] font-medium text-foreground">
                      {serial}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <span className="font-mono text-xs">{serial}</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  void copyToClipboard(serial).then(() => {
                    toast.success(t('common.copied'));
                  });
                }}
                className="opacity-40 transition-opacity hover:opacity-100 focus:opacity-100 group-hover/row:opacity-100 group-hover:opacity-100"
                aria-label={t('licenses.table.copySerialAria', { serial })}
                title={t('common.copySerial')}
                data-testid={`copy-serial-${serial}`}
              >
                <Copy className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
              {pendingLicenseIds.has(row.original.id) && (
                <Link
                  to="/fingerprint-requests"
                  onClick={(e) => e.stopPropagation()}
                  title={t('fingerprintRequests.badgeLabel')}
                  aria-label={t('fingerprintRequests.badgeLabel')}
                  className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-600 dark:text-amber-400"
                >
                  <Fingerprint className="h-3 w-3" aria-hidden="true" />
                  {t('fingerprintRequests.badgeLabel')}
                </Link>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'type',
        enableSorting: false,
        header: t('licenses.table.type'),
        cell: ({ row }) => (
          <span className="text-[13.5px] capitalize text-muted-foreground">
            {row.original.type}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: t('licenses.table.status'),
        cell: ({ row }) => <StatusWord status={row.original.status} />,
      },
      {
        accessorKey: 'activationCount',
        enableSorting: false,
        header: t('licenses.table.activations'),
        cell: ({ row }) => (
          <span className="font-mono text-[13px] tabular-nums text-muted-foreground">
            {row.original.activationCount}
          </span>
        ),
      },
      {
        accessorKey: 'expiresAt',
        header: t('licenses.table.expires'),
        cell: ({ row }) => (
          <span className="text-[13.5px] text-muted-foreground">
            <RelativeTime date={row.original.expiresAt} />
          </span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: t('licenses.table.created'),
        cell: ({ row }) => (
          <span className="text-[13.5px] text-muted-foreground">
            <RelativeTime date={row.original.createdAt} />
          </span>
        ),
      },
    ],
    [t, pendingLicenseIds]
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
  });

  if (isMobile) {
    return (
      <ul className="border-t">
        {data.map((license) => (
          <li key={license.id} className="border-b">
            <Link
              to={`/licenses/${license.id}`}
              className="flex items-center gap-3 px-1 py-4 transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-mono text-[13.5px] font-medium text-foreground">
                  {license.serial}
                </span>
                <span className="mt-1 block text-[13px] text-faint">
                  {t('licenses.table.expires')}: <RelativeTime date={license.expiresAt} />
                </span>
              </span>
              <StatusWord status={license.status} />
              <ChevronRight className="h-4 w-4 shrink-0 text-faint" aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-background">
        {table.getHeaderGroups().map((hg) => (
          <TableRow key={hg.id} className="bg-foreground/[0.03] hover:bg-foreground/[0.03]">
            {hg.headers.map((h) => (
              <TableHead key={h.id} className="h-11 px-3 text-[13px] font-medium text-faint">
                {h.column.getCanSort() ? (
                  <button
                    className="flex items-center gap-1.5 rounded-sm font-medium transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={h.column.getToggleSortingHandler()}
                    aria-sort={
                      h.column.getIsSorted() === 'asc'
                        ? 'ascending'
                        : h.column.getIsSorted() === 'desc'
                          ? 'descending'
                          : 'none'
                    }
                  >
                    {flexRender(h.column.columnDef.header, h.getContext())}
                    <SortIndicator sorted={h.column.getIsSorted()} />
                  </button>
                ) : (
                  flexRender(h.column.columnDef.header, h.getContext())
                )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow
            key={row.id}
            tabIndex={0}
            role="link"
            className="group/row cursor-pointer hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => navigate(`/licenses/${row.original.id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                navigate(`/licenses/${row.original.id}`);
              }
            }}
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id} className="px-3 py-3">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
